import { Check, Error, Info, Warning } from '@mui/icons-material';
import { Alert, AlertTitle, Typography } from '@mui/material';
import { BASE_COLORS, hexToRGBA } from 'lib/functions/const/colors';
import React, { useMemo } from 'react';

type Props = {
  severity?: 'error' | 'success' | 'info' | 'warning';
  title?: string;
  message?: React.ReactNode;
  action?: React.ReactNode;
};

const AlertBanner: React.FC<Props> = ({ severity, title, message, action }) => {
  const color = useMemo(() => {
    switch (severity) {
      case 'error':
        return BASE_COLORS.red;
      case 'success':
        return BASE_COLORS.green;
      case 'info':
        return BASE_COLORS.blue;
      case 'warning':
        return BASE_COLORS.yellow;
      default:
        return BASE_COLORS.blue;
    }
  }, [severity]);

  const icon = useMemo(() => {
    switch (severity) {
      case 'error':
        return <Error sx={{ fontSize: '1rem' }} />;
      case 'success':
        return <Check sx={{ fontSize: '1rem' }} />;
      case 'info':
        return <Info sx={{ fontSize: '1rem' }} />;
      case 'warning':
        return <Warning sx={{ fontSize: '1rem' }} />;
    }
  }, [severity]);

  return (
    <Alert
      severity={severity}
      icon={icon}
      sx={{
        borderRadius: 1,
        py: 1.25,
        backgroundColor: hexToRGBA(color, 0.04),
        border: `1px solid ${hexToRGBA(color, 0.2)}`,
        '& .MuiAlert-message': { py: 0.25 },
      }}
      action={action}
    >
      <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
        {title}
      </AlertTitle>
      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
        {message}
      </Typography>
    </Alert>
  );
};

export default AlertBanner;
